import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  AlertCircle,
  Check,
  X,
  Mail,
  MailCheck,
  Eye,
  User,
  Calendar,
  FileText,
  Plus,
  Loader2,
} from "lucide-react";

const AdminInvoices = () => {
  const { user, token, API, isSuperAdmin } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Create invoice state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    client_id: "",
    amount: "",
    description: "",
    due_days: 30,
  });
  
  // Preview state
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchInvoices();
    if (isSuperAdmin) {
      fetchClients();
    }
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const [allRes, pendingRes] = await Promise.all([
        fetch(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/invoices/pending-approval`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (allRes.ok) {
        const data = await allRes.json();
        setInvoices(data);
      }

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingApproval(data);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!newInvoice.client_id || !newInvoice.amount || !newInvoice.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreating(true);
    try {
      const params = new URLSearchParams({
        client_id: newInvoice.client_id,
        amount: newInvoice.amount.toString(),
        description: newInvoice.description,
        due_days: newInvoice.due_days.toString(),
      });
      
      const response = await fetch(`${API}/admin/invoices/create?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Invoice created successfully");
        setCreateDialogOpen(false);
        setPreviewMode(false);
        setPreviewData(null);
        setNewInvoice({ client_id: "", amount: "", description: "", due_days: 30 });
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create invoice");
      }
    } catch (error) {
      toast.error("Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const handlePreviewInvoice = async () => {
    if (!newInvoice.client_id || !newInvoice.amount || !newInvoice.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        client_id: newInvoice.client_id,
        amount: newInvoice.amount.toString(),
        description: newInvoice.description,
        due_days: newInvoice.due_days.toString(),
      });
      
      const response = await fetch(`${API}/admin/invoices/preview?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setPreviewMode(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate preview");
      }
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApprove = async (invoiceId) => {
    setProcessing(invoiceId);
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Invoice approved and client notified");
        fetchInvoices();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to approve invoice");
      }
    } catch (error) {
      toast.error("Failed to approve invoice");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!selectedInvoice) return;
    
    setProcessing(selectedInvoice.invoice_id);
    try {
      const params = new URLSearchParams();
      if (rejectReason) params.append("reason", rejectReason);
      
      const response = await fetch(
        `${API}/admin/invoices/${selectedInvoice.invoice_id}/reject?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success("Invoice rejected");
        setRejectDialogOpen(false);
        setSelectedInvoice(null);
        setRejectReason("");
        fetchInvoices();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to reject invoice");
      }
    } catch (error) {
      toast.error("Failed to reject invoice");
    } finally {
      setProcessing(null);
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to download PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleSendEmail = async (invoiceId) => {
    setProcessing(invoiceId);
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invoice sent to ${data.email}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to send invoice email");
    } finally {
      setProcessing(null);
    }
  };

  const handleResendReceipt = async (invoiceId) => {
    setProcessing(invoiceId);
    try {
      const response = await fetch(`${API}/admin/invoices/${invoiceId}/resend-receipt`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Payment receipt sent");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to resend receipt");
      }
    } catch {
      toast.error("Failed to resend receipt");
    } finally {
      setProcessing(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending_approval":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Pending Approval</Badge>;
      case "pending":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Awaiting Payment</Badge>;
      case "paid":
        return <Badge variant="secondary" className="bg-green-50 text-green-700">Paid</Badge>;
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleExportInvoices = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/admin/export/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = response.headers.get("Content-Disposition")?.split("filename=")[1] || "invoices_export.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Invoices exported successfully");
      } else {
        toast.error("Failed to export invoices");
      }
    } catch (error) {
      toast.error("Failed to export invoices");
    } finally {
      setExporting(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    setProcessing(invoiceId);
    try {
      const params = new URLSearchParams({ new_status: newStatus });
      const response = await fetch(
        `${API}/admin/invoices/${invoiceId}/update-status?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update invoice status");
    } finally {
      setProcessing(null);
    }
  };

  // Stats
  const stats = {
    pendingApproval: pendingApproval.length,
    awaitingPayment: invoices.filter((i) => i.status === "pending").length,
    paid: invoices.filter((i) => i.status === "paid").length,
    totalOutstanding: invoices
      .filter((i) => i.status === "pending")
      .reduce((sum, i) => sum + i.amount, 0),
  };

  return (
    <AdminLayout activeNav="invoices">
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight">
              Invoices
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Review, approve, and manage client invoices
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-rnb-navy hover:bg-rnb-navy/90 gap-1.5 text-xs h-9"
                size="sm"
                data-testid="create-invoice-btn"
              >
                <Plus className="h-3.5 w-3.5" />
                Create Invoice
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportInvoices}
              disabled={exporting}
              className="text-xs gap-1.5 h-9 border-gray-200 text-gray-500 hover:text-rnb-navy"
              data-testid="export-invoices-btn"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? "Exporting..." : "Export"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats — colorful gradient icons */}
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Pending Approval", value: stats.pendingApproval, gradient: "from-amber-400 to-orange-500", icon: Clock },
                { label: "Awaiting Payment", value: stats.awaitingPayment, gradient: "from-rnb-blue to-sky-500", icon: DollarSign },
                { label: "Paid", value: stats.paid, gradient: "from-emerald-500 to-rnb-teal", icon: CheckCircle },
                { label: "Outstanding", value: formatCurrency(stats.totalOutstanding), gradient: "from-rnb-navy to-rnb-blue", icon: DollarSign },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-4 bg-white rounded-xl p-5">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow-sm`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-accent text-xl font-bold text-rnb-navy">{s.value}</p>
                    <p className="text-xs text-gray-400">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pending Approval Section */}
            {pendingApproval.length > 0 && (
              <div className="mb-6" data-testid="pending-approval-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <AlertCircle className="h-3.5 w-3.5 text-white" />
                  </div>
                  <h2 className="text-sm font-semibold text-rnb-navy">
                    Pending Approval ({pendingApproval.length})
                  </h2>
                </div>
                <div className="space-y-2">
                    {pendingApproval.map((inv) => (
                      <div
                        key={inv.invoice_id}
                        className="bg-white rounded-xl px-5 py-4 flex items-center justify-between hover:shadow-md transition-all duration-200"
                        data-testid={`pending-invoice-${inv.invoice_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <p className="font-medium text-rnb-navy">{inv.client_name}</p>
                            <Badge variant="outline" className="text-xs">
                              Created by {inv.created_by_name}
                            </Badge>
                          </div>
                          <p className="text-sm text-rnb-gray">{inv.description}</p>
                          <p className="text-xs text-rnb-gray mt-1">
                            Due: {formatDate(inv.due_date)} • Created: {formatDate(inv.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="font-mono-numbers text-xl font-bold text-rnb-navy">
                            {formatCurrency(inv.amount)}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInvoice(inv);
                                setViewDialogOpen(true);
                              }}
                              data-testid={`view-${inv.invoice_id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(inv.invoice_id)}
                              disabled={processing === inv.invoice_id}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-${inv.invoice_id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInvoice(inv);
                                setRejectDialogOpen(true);
                              }}
                              disabled={processing === inv.invoice_id}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`reject-${inv.invoice_id}`}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* All Invoices */}
            <div data-testid="all-invoices-section">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-rnb-navy to-rnb-blue flex items-center justify-center">
                  <DollarSign className="h-3.5 w-3.5 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-rnb-navy">All Invoices</h2>
              </div>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                    {invoices.map((inv) => (
                      <div
                        key={inv.invoice_id}
                        className="bg-white rounded-xl px-5 py-4 flex items-center justify-between hover:shadow-md transition-all duration-200"
                        data-testid={`invoice-${inv.invoice_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <p className="font-medium text-rnb-navy">{inv.client_name}</p>
                            {getStatusBadge(inv.status)}
                          </div>
                          <p className="text-sm text-rnb-gray">{inv.description}</p>
                          <p className="text-xs text-rnb-gray mt-1">
                            Due: {formatDate(inv.due_date)}
                            {inv.paid_at && ` • Paid: ${formatDate(inv.paid_at)}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="font-mono-numbers text-lg font-semibold text-rnb-navy">
                            {formatCurrency(inv.amount)}
                          </p>
                          <div className="flex gap-1 items-center">
                            {/* Status change dropdown for super admin */}
                            {isSuperAdmin && inv.status !== "pending_approval" && (
                              <Select
                                value={inv.status}
                                onValueChange={(value) => handleUpdateStatus(inv.invoice_id, value)}
                                disabled={processing === inv.invoice_id}
                              >
                                <SelectTrigger 
                                  className="w-[120px] h-8 text-xs"
                                  data-testid={`status-select-${inv.invoice_id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="paid">Paid</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPdf(inv.invoice_id)}
                              title="Download PDF"
                              data-testid={`download-${inv.invoice_id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {["pending", "paid"].includes(inv.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendEmail(inv.invoice_id)}
                                disabled={processing === inv.invoice_id}
                                title="Send via Email"
                                data-testid={`email-${inv.invoice_id}`}
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            )}
                            {inv.status === "paid" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResendReceipt(inv.invoice_id)}
                                disabled={processing === inv.invoice_id}
                                title="Resend Payment Receipt"
                                data-testid={`resend-receipt-${inv.invoice_id}`}
                              >
                                <MailCheck className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl py-12 text-center">
                    <DollarSign className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                    <p className="text-sm text-gray-400">No invoices found</p>
                  </div>
                )}
            </div>
          </>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Invoice</DialogTitle>
              <DialogDescription>
                Are you sure you want to reject this invoice? The creator will be notified.
              </DialogDescription>
            </DialogHeader>
            {selectedInvoice && (
              <div className="py-4">
                <div className="bg-slate-50 p-4 rounded-lg mb-4">
                  <p className="font-medium text-rnb-navy">{selectedInvoice.client_name}</p>
                  <p className="text-xl font-bold text-rnb-navy mt-1">
                    {formatCurrency(selectedInvoice.amount)}
                  </p>
                  <p className="text-sm text-rnb-gray mt-1">{selectedInvoice.description}</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-rnb-navy">
                    Reason for rejection (optional)
                  </label>
                  <Input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter reason..."
                    data-testid="reject-reason"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={processing === selectedInvoice?.invoice_id}
                data-testid="confirm-reject"
              >
                Reject Invoice
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Invoice Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-rnb-blue" />
                Invoice Details
              </DialogTitle>
              <DialogDescription>
                Review the full invoice details before approving or rejecting.
              </DialogDescription>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-6 py-4">
                {/* Client Info */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-rnb-gray">Client</p>
                      <p className="font-semibold text-rnb-navy text-lg">{selectedInvoice.client_name}</p>
                    </div>
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                      Pending Approval
                    </Badge>
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-sm text-rnb-gray">Created by</p>
                    <p className="font-medium text-rnb-navy">{selectedInvoice.created_by_name || "Admin"}</p>
                  </div>
                </div>

                {/* Amount Section */}
                <div className="bg-rnb-blue/5 p-4 rounded-lg border border-rnb-blue/20">
                  <p className="text-sm text-rnb-gray mb-1">Total Amount</p>
                  <p className="font-mono-numbers text-3xl font-bold text-rnb-navy">
                    {formatCurrency(selectedInvoice.amount)}
                  </p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedInvoice.consultant_name && (
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-rnb-gray mt-0.5" />
                      <div>
                        <p className="text-sm text-rnb-gray">Consultant</p>
                        <p className="font-medium text-rnb-navy">{selectedInvoice.consultant_name}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedInvoice.hours_worked && (
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-rnb-gray mt-0.5" />
                      <div>
                        <p className="text-sm text-rnb-gray">Hours Worked</p>
                        <p className="font-medium text-rnb-navy">{selectedInvoice.hours_worked} hours</p>
                      </div>
                    </div>
                  )}

                  {(selectedInvoice.period_start || selectedInvoice.period_end) && (
                    <div className="flex items-start gap-3 col-span-2">
                      <Calendar className="h-5 w-5 text-rnb-gray mt-0.5" />
                      <div>
                        <p className="text-sm text-rnb-gray">Billing Period</p>
                        <p className="font-medium text-rnb-navy">
                          {selectedInvoice.period_start && formatDate(selectedInvoice.period_start)}
                          {selectedInvoice.period_start && selectedInvoice.period_end && " - "}
                          {selectedInvoice.period_end && formatDate(selectedInvoice.period_end)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-rnb-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-rnb-gray">Due Date</p>
                      <p className="font-medium text-rnb-navy">{formatDate(selectedInvoice.due_date)}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-rnb-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-rnb-gray">Created</p>
                      <p className="font-medium text-rnb-navy">{formatDate(selectedInvoice.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <p className="text-sm text-rnb-gray mb-2">Description</p>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-rnb-navy whitespace-pre-wrap">{selectedInvoice.description}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleApprove(selectedInvoice.invoice_id);
                      setViewDialogOpen(false);
                    }}
                    disabled={processing === selectedInvoice.invoice_id}
                    data-testid="view-dialog-approve"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Approve Invoice
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      setViewDialogOpen(false);
                      setRejectDialogOpen(true);
                    }}
                    disabled={processing === selectedInvoice.invoice_id}
                    data-testid="view-dialog-reject"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject Invoice
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Invoice Dialog with Preview */}
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setPreviewMode(false);
            setPreviewData(null);
          }
        }}>
          <DialogContent className={previewMode ? "max-w-2xl" : "max-w-md"}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {previewMode ? (
                  <>
                    <Eye className="h-5 w-5 text-rnb-blue" />
                    Invoice Preview
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5 text-[#2471A3]" />
                    Create New Invoice
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {previewMode
                  ? "Review the invoice details and the email the client will receive."
                  : "Create an invoice to send to a client for payment."}
              </DialogDescription>
            </DialogHeader>

            {previewMode && previewData ? (
              <div className="space-y-4 py-2" data-testid="invoice-preview-panel">
                {/* Preview Header Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <p className="text-xs text-gray-400 mb-1">Recipient</p>
                    <p className="text-sm font-semibold text-rnb-navy">{previewData.client_name}</p>
                    <p className="text-xs text-gray-500">{previewData.client_email}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl">
                    <p className="text-xs text-gray-400 mb-1">Amount</p>
                    <p className="text-xl font-bold text-rnb-navy">{formatCurrency(previewData.amount)}</p>
                    <p className="text-xs text-gray-500">Due: {previewData.due_date}</p>
                  </div>
                </div>
                
                {/* Email Subject */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Email Subject</p>
                  <p className="text-sm font-medium text-rnb-navy">{previewData.email_subject}</p>
                </div>
                
                {/* Email Preview */}
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-gray-400" />
                    <p className="text-xs font-medium text-gray-500">Email Preview</p>
                  </div>
                  <div
                    className="p-4 bg-white max-h-[320px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.email_html) }}
                    data-testid="email-preview-content"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div>
                  <Label>Client *</Label>
                  <Select
                    value={newInvoice.client_id}
                    onValueChange={(value) => setNewInvoice({ ...newInvoice, client_id: value })}
                  >
                    <SelectTrigger data-testid="invoice-client-select">
                      <SelectValue placeholder="Select a client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.company_name || client.name || client.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Amount ($) *</Label>
                  <Input
                    type="number"
                    value={newInvoice.amount}
                    onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    data-testid="invoice-amount-input"
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={newInvoice.description}
                    onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                    placeholder="Consulting services for..."
                    rows={3}
                    data-testid="invoice-description-input"
                  />
                </div>
                
                <div>
                  <Label>Due in (days)</Label>
                  <Select
                    value={newInvoice.due_days.toString()}
                    onValueChange={(value) => setNewInvoice({ ...newInvoice, due_days: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="invoice-due-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="45">45 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              {previewMode ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setPreviewMode(false); setPreviewData(null); }}
                    data-testid="back-to-edit-btn"
                  >
                    Back to Edit
                  </Button>
                  <Button
                    onClick={handleCreateInvoice}
                    disabled={creating}
                    className="bg-rnb-teal hover:bg-rnb-teal/90"
                    data-testid="confirm-send-invoice-btn"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Confirm & Send
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handlePreviewInvoice}
                    disabled={loadingPreview}
                    className="gap-1.5"
                    data-testid="preview-invoice-btn"
                  >
                    {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    Preview
                  </Button>
                  <Button
                    onClick={handleCreateInvoice}
                    disabled={creating}
                    className="bg-[#2471A3] hover:bg-[#1A5276]"
                    data-testid="create-invoice-submit"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create Invoice
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminInvoices;
